/**
 * Projects Page — Student-facing projects hub
 * Categories, difficulty filter, paid access gating, mark as done
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  FolderGit2, Lock, CheckCircle, Clock, ArrowLeft, Filter,
  ChevronRight, ChevronLeft, ShieldCheck, Layers
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const difficultyConfig = {
  beginner: { color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', label: 'Beginner' },
  intermediate: { color: 'bg-amber-500/20 text-amber-300 border-amber-500/30', label: 'Intermediate' },
  advanced: { color: 'bg-red-500/20 text-red-300 border-red-500/30', label: 'Advanced' },
};

const Projects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isIndia } = useCurrency();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasedCount, setPurchasedCount] = useState(0);
  const [completedIds, setCompletedIds] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  const getToken = () => localStorage.getItem('authToken');
  const headers = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/projects`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects);
        setPurchasedCount(data.purchased_count || 0);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/projects/progress/my`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setCompletedIds(data.completed);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (user) { fetchProjects(); fetchProgress(); }
  }, [user]);

  const openProject = async (project) => {
    if (project.locked) {
      navigate(`/payment/PROJECT_${project.id}`);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/projects/${project.id}`, { headers: headers() });
      if (res.ok) setSelectedProject(await res.json());
      else toast.error('Failed to load project');
    } catch (e) { toast.error('Failed to load'); }
    finally { setDetailLoading(false); }
  };

  const toggleComplete = async (projectId) => {
    const isCompleted = completedIds.includes(projectId);
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/complete`, {
        method: isCompleted ? 'DELETE' : 'POST', headers: headers()
      });
      if (res.ok) {
        if (isCompleted) {
          setCompletedIds(prev => prev.filter(id => id !== projectId));
          toast.success('Unmarked');
        } else {
          setCompletedIds(prev => [...prev, projectId]);
          toast.success('Marked as complete!');
        }
      }
    } catch (e) { toast.error('Failed'); }
  };

  const categories = ['all', ...new Set(projects.map(p => p.category))];
  const filtered = projects.filter(p =>
    (categoryFilter === 'all' || p.category === categoryFilter) &&
    (difficultyFilter === 'all' || p.difficulty === difficultyFilter)
  );

  const completedCount = completedIds.length;
  const totalEnabled = projects.length;

  // Parse content into sections
  const parseContentSections = (content) => {
    if (!content) return [];
    const lines = content.split('\n');
    const sections = [];
    let currentSection = null;

    for (const line of lines) {
      const trimmed = line.trim();
      const isHeader = (
        (trimmed.length > 3 && trimmed.length < 80 && trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed) && !trimmed.startsWith('-') && !trimmed.startsWith('*')) ||
        /^PHASE\s+\d+/i.test(trimmed) ||
        /^Step\s+\d+/i.test(trimmed)
      );
      if (isHeader && trimmed.length > 0) {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: trimmed, content: '' };
      } else if (currentSection) {
        currentSection.content += line + '\n';
      } else {
        if (!sections.length && !currentSection) {
          currentSection = { title: 'Overview', content: line + '\n' };
        }
      }
    }
    if (currentSection) sections.push(currentSection);
    // Merge empty sections into previous
    const merged = [];
    for (const sec of sections) {
      const contentLines = sec.content.trim().split('\n').filter(l => l.trim());
      if (merged.length > 0 && contentLines.length === 0) {
        merged[merged.length - 1].content += '\n' + sec.title + '\n';
      } else {
        merged.push(sec);
      }
    }
    return merged.length > 0 ? merged : [{ title: 'Project Guide', content: content }];
  };

  const [activeStep, setActiveStep] = useState(0);

  if (selectedProject) {
    // Use sections array if available, else parse from content
    const sections = (selectedProject.sections && selectedProject.sections.length > 0)
      ? selectedProject.sections
      : parseContentSections(selectedProject.content);
    const totalSteps = sections.length;
    const progress = totalSteps > 1 ? Math.round(((activeStep + 1) / totalSteps) * 100) : 100;
    const currentSection = sections[activeStep] || sections[0];
    const phaseColors = [
      'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500',
      'bg-red-500', 'bg-cyan-500', 'bg-pink-500', 'bg-orange-500'
    ];
    const phaseTextColors = [
      'text-blue-400', 'text-emerald-400', 'text-amber-400', 'text-purple-400',
      'text-red-400', 'text-cyan-400', 'text-pink-400', 'text-orange-400'
    ];

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={() => { setSelectedProject(null); setActiveStep(0); }} className="text-slate-300 hover:text-white" data-testid="back-to-projects">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">{activeStep + 1} / {totalSteps}</span>
              <Button
                size="sm"
                onClick={() => toggleComplete(selectedProject.id)}
                className={completedIds.includes(selectedProject.id)
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'bg-white/10 text-slate-300 border border-white/20'}
                data-testid="mark-complete-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {completedIds.includes(selectedProject.id) ? 'Done' : 'Mark Done'}
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* Project Title Bar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Badge className={difficultyConfig[selectedProject.difficulty]?.color || 'bg-slate-500/20 text-slate-300'}>
              {difficultyConfig[selectedProject.difficulty]?.label || selectedProject.difficulty}
            </Badge>
            <Badge className="bg-blue-500/20 text-blue-300">{selectedProject.category}</Badge>
            {selectedProject.estimated_time && <Badge className="bg-white/10 text-slate-300"><Clock className="w-3 h-3 mr-1" />{selectedProject.estimated_time}</Badge>}
            <h2 className="text-white font-semibold text-base ml-1" data-testid="project-detail-title">{selectedProject.title}</h2>
          </div>

          {/* Main Layout: Sidebar + Content */}
          <div className="flex gap-4">
            {/* Sidebar — Section Navigator */}
            <div className="hidden md:block w-64 flex-shrink-0">
              <div className="sticky top-6 bg-white/5 border border-white/10 rounded-xl p-3 max-h-[70vh] overflow-y-auto">
                <p className="text-xs text-slate-500 font-medium mb-2 px-2">SECTIONS</p>
                {sections.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveStep(idx)}
                    className={`w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs transition-all mb-0.5 ${
                      idx === activeStep
                        ? 'bg-white/10 text-white font-medium'
                        : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                    }`}
                    data-testid={`step-nav-${idx}`}
                  >
                    <span className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                      idx === activeStep ? `${phaseColors[idx % phaseColors.length]} text-white` : 'bg-white/10 text-slate-500'
                    }`}>{idx + 1}</span>
                    <span className="truncate">{s.title.length > 28 ? s.title.slice(0, 28) + '...' : s.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-w-0">
              {/* Mobile step selector */}
              <div className="md:hidden mb-3">
                <select
                  value={activeStep}
                  onChange={e => setActiveStep(Number(e.target.value))}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm"
                  data-testid="step-mobile-select"
                >
                  {sections.map((s, idx) => (
                    <option key={idx} value={idx} className="bg-slate-800">{idx + 1}. {s.title}</option>
                  ))}
                </select>
              </div>

              {/* Section Header */}
              <div className="flex items-center gap-3 mb-4">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${phaseColors[activeStep % phaseColors.length]} text-white`}>{activeStep + 1}</span>
                <h3 className={`text-lg font-bold ${phaseTextColors[activeStep % phaseTextColors.length]}`}>{currentSection.title}</h3>
              </div>

              {/* Section Content */}
              <Card className="bg-white/5 border-white/10 backdrop-blur">
                <CardContent className="p-5">
                  <pre className="text-slate-300 whitespace-pre-wrap text-sm leading-relaxed font-mono" data-testid="project-content">{currentSection.content.trim()}</pre>
                </CardContent>
              </Card>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                  disabled={activeStep === 0}
                  className="border-white/20 text-slate-300 disabled:opacity-30"
                  data-testid="prev-step-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />Previous
                </Button>

                {activeStep < totalSteps - 1 ? (
                  <Button
                    onClick={() => setActiveStep(activeStep + 1)}
                    className="bg-gradient-to-r from-orange-500 to-red-500 text-white"
                    data-testid="next-step-btn"
                  >
                    Next<ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => toggleComplete(selectedProject.id)}
                    className={completedIds.includes(selectedProject.id)
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'bg-gradient-to-r from-emerald-500 to-green-500 text-white'}
                    data-testid="mark-complete-btn-bottom"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {completedIds.includes(selectedProject.id) ? 'Completed!' : 'Finish Project'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3" data-testid="projects-heading">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                <FolderGit2 className="w-6 h-6 text-white" />
              </div>
              Projects Hub
            </h1>
            <p className="text-slate-400 mt-2">Real-world projects to build your skills. Practice on your own VM/AWS environment.</p>
          </div>
          {purchasedCount > 0 ? (
            <Badge className="bg-emerald-500/20 text-emerald-300 py-2 px-4 text-sm" data-testid="access-badge">
              <ShieldCheck className="w-4 h-4 mr-2" />{purchasedCount} Purchased
            </Badge>
          ) : (
            <Badge className="bg-white/10 text-slate-400 py-2 px-4 text-sm">
              <Lock className="w-4 h-4 mr-2" />Per Project
            </Badge>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <Layers className="w-8 h-8 text-blue-400" />
              <div><p className="text-xs text-blue-300">Total Projects</p><p className="text-xl font-bold text-white">{totalEnabled}</p></div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
              <div><p className="text-xs text-emerald-300">Completed</p><p className="text-xl font-bold text-white">{completedCount}</p></div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-amber-400" />
              <div><p className="text-xs text-amber-300">Purchased</p><p className="text-xl font-bold text-white">{purchasedCount}</p></div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <Filter className="w-8 h-8 text-purple-400" />
              <div><p className="text-xs text-purple-300">Showing</p><p className="text-xl font-bold text-white">{filtered.length}</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500 self-center mr-1">Category:</span>
            {categories.map(c => (
              <button key={c} onClick={() => setCategoryFilter(c)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${categoryFilter === c ? 'bg-orange-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'}`}
                data-testid={`filter-category-${c}`}
              >
                {c === 'all' ? 'All' : c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500 self-center mr-1">Difficulty:</span>
            {['all', 'beginner', 'intermediate', 'advanced'].map(d => (
              <button key={d} onClick={() => setDifficultyFilter(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${difficultyFilter === d ? 'bg-orange-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'}`}
                data-testid={`filter-difficulty-${d}`}
              >
                {d === 'all' ? 'All' : d.charAt(0).toUpperCase() + d.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <FolderGit2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No projects found</p>
            <p className="text-slate-500 text-sm mt-1">Projects will appear here once admin adds them.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(p => {
              const isCompleted = completedIds.includes(p.id);
              return (
                <Card
                  key={p.id}
                  className={`bg-white/5 border-white/10 backdrop-blur hover:bg-white/8 transition-all cursor-pointer group ${isCompleted ? 'ring-1 ring-emerald-500/30' : ''}`}
                  onClick={() => openProject(p)}
                  data-testid={`project-card-${p.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex flex-wrap gap-2">
                        <Badge className={difficultyConfig[p.difficulty]?.color || 'bg-slate-500/20 text-slate-300'}>
                          {difficultyConfig[p.difficulty]?.label || p.difficulty}
                        </Badge>
                        <Badge className="bg-blue-500/20 text-blue-300 text-xs">{p.category}</Badge>
                      </div>
                      {p.locked ? (
                        <Lock className="w-5 h-5 text-slate-500" />
                      ) : isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
                      )}
                    </div>
                    <h3 className="text-white font-semibold mb-2 group-hover:text-orange-300 transition-colors">{p.title}</h3>
                    <p className="text-slate-400 text-sm line-clamp-2 mb-3">{p.short_description}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {p.estimated_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{p.estimated_time}</span>}
                      {p.is_free && <Badge className="bg-emerald-500/20 text-emerald-300 text-xs">Free</Badge>}
                      {p.locked && !p.is_free && <Badge className="bg-orange-500/20 text-orange-300 text-xs font-semibold">{isIndia ? '₹99' : '$2'}</Badge>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
